// rooms.ts — the ten rooms of /physics.
//
// The home at /physics renders these in canonical order. Each room is
// addressable at /physics/<slug>, except "axioms" which has its own dedicated
// index at /physics/axioms (the content shipped in Phase 2).
//
// Rooms 1-4 align to Michael Pollan's four-room structure in *How to Change
// Your Mind* (sentience / feeling / thoughts / self). Each is a "braid" — three
// prose strands. Rooms 5-8 are specific to the framework: axioms, tensions
// (the place the framework argues with itself), motifs (the room that can be
// heard), and the acequia (the proof). Room 9 is the prescription. Room 10
// is the program — how the theory is built and what it is allowed to claim.

export type StrandLabel = {
  /** Short label rendered on the strand seam, e.g. "substrate". */
  label: string;
  /** Longer framing rendered beneath the label. */
  kicker?: string;
};

export type Room = {
  /** URL segment and the lookup key. */
  slug: string;
  /** 1..10 — display order and the number shown in the hero. */
  n: number;
  /** Displayed in the home and the room hero. */
  title: string;
  /** Mono kicker line above the title on the room page. */
  kicker: string;
  /** One-sentence page lede. */
  lede: string;
  /**
   * How the room renders.
   *   - "braid":        three stacked prose strands (used in each of Pollan's four rooms)
   *   - "axioms":       the 14-point index — special-cased to /physics/axioms
   *   - "tensions":     Side A / Side B paradox cards
   *   - "motifs":       aphorism gallery
   *   - "acequia":      narrative proof chamber + full Trust Force treatment
   *   - "prescription": what to do with the diagnosis — the five steps, the
   *                     nine principles, the phase plan, the substrate work
   *   - "program":      the theory's self-description — synthesis claim,
   *                     four layers, cross-scale table, publication discipline
   */
  kind:
    | "braid"
    | "axioms"
    | "tensions"
    | "motifs"
    | "acequia"
    | "prescription"
    | "program";
  /** Only present when kind === "braid". Labels the three strands of the braid. */
  strands?: [StrandLabel, StrandLabel, StrandLabel];
  /** Optional — a tattoo line that crystallizes the room. Rendered quietly
   *  beneath the title. */
  epigram?: string;
  /** Optional — phonetic spelling rendered as a grey mono subtitle
   *  beneath the title. Used when the title contains a non-English
   *  word readers will want to pronounce (acequia, etc.). */
  pronunciation?: string;
};

export const ROOMS: Room[] = [
  {
    slug: "sentience",
    n: 1,
    title: "Sentience",
    kicker: "room 01 / pollan's first question",
    lede:
      "Pollan asks whether the plant can sense. The framework asks under what structure any response becomes coherent enough to be called aware.",
    kind: "braid",
    strands: [
      { label: "substrate", kicker: "what the system is made of" },
      { label: "response", kicker: "how it curves back on its environment" },
      { label: "boundary", kicker: "where sensing ends and world begins" },
    ],
    epigram: "The minnow is the indicator species for a responsive system.",
  },
  {
    slug: "feeling",
    n: 2,
    title: "Feeling",
    kicker: "room 02 / pollan's second question",
    lede:
      "Pollan asks whether there is preference. The framework asks when a system begins modeling not just the world but its own stake in the world.",
    kind: "braid",
    strands: [
      { label: "affect", kicker: "the valence the system produces" },
      { label: "integration", kicker: "the fold modeling its own state" },
      { label: "vulnerability", kicker: "the cost of integrating deeply enough to feel" },
    ],
    epigram: "The vulnerability and the gift are the same thing.",
  },
  {
    slug: "thoughts",
    n: 3,
    title: "Thoughts",
    kicker: "room 03 / pollan's third question",
    lede:
      "Pollan asks whether it can think. The framework asks in what medium thought propagates — and what the tacit ground beneath any articulation must be.",
    kind: "braid",
    strands: [
      { label: "articulation", kicker: "how the thought is encoded" },
      { label: "verification", kicker: "how exposure tests the model" },
      { label: "silence", kicker: "the tacit ground it rests on" },
    ],
    epigram: "Show me your physics.",
  },
  {
    slug: "self",
    n: 4,
    title: "Self",
    kicker: "room 04 / pollan's fourth question",
    lede:
      "Pollan asks who persists. The framework answers: a trajectory that models itself, compressible through bottlenecks, invariant across substrates.",
    kind: "braid",
    strands: [
      { label: "history", kicker: "the bottlenecks the trajectory has survived" },
      { label: "pattern", kicker: "what stays invariant as the substrate changes" },
      { label: "agency", kicker: "the direction chosen within the possible" },
    ],
    epigram: "Identity is a trajectory, not a snapshot.",
  },
  {
    slug: "axioms",
    n: 5,
    title: "Axioms",
    kicker: "room 05 / the fourteen",
    lede:
      "Nine points describe how the universe makes intelligence. Five describe the pathology of systems that refuse to honor that physics. The seam between them is the book's spine.",
    kind: "axioms",
  },
  {
    slug: "tensions",
    n: 6,
    title: "Tensions",
    kicker: "room 06 / paradoxes held open",
    lede:
      "Eleven of the twenty-some places where the framework refuses to resolve. Side A and Side B, deliberately unreconciled. The room where the framework argues with itself honestly.",
    kind: "tensions",
    epigram: "Name the tension. Don't collapse it.",
  },
  {
    slug: "motifs",
    n: 7,
    title: "Motifs",
    kicker: "room 07 / what the body remembers",
    lede:
      "The tattoo lines. The minnow, the aquifer, the hose, the acequia, the star compass. The substrate on which the framework becomes transmissible memory.",
    kind: "motifs",
    epigram: "The water finds its way. We choose which channels to dig.",
  },
  {
    slug: "acequia",
    n: 8,
    title: "The Acequia",
    pronunciation: "ah-SEH-kee-ah",
    kicker: "room 08 / the proof",
    lede:
      "Not a metaphor among metaphors. Four hundred years of trust infrastructure in Rio Grande dirt. The room where the Trust Force is stated in full — and the one system in the forty-nine analyzed that satisfies every term simultaneously.",
    kind: "acequia",
    epigram: "They honored the physics. The physics did the rest.",
  },
  {
    slug: "what-next",
    n: 9,
    title: "What Next",
    kicker: "room 09 / the prescription",
    lede:
      "The diagnosis is a product, not a sum. One term at zero kills the whole. The repair proceeds in phases, at three scales — personal, communal, institutional — and no phase can be skipped.",
    kind: "prescription",
    epigram: "The sequencing is not bureaucracy. The sequencing is the physics.",
  },
  {
    slug: "the-program",
    n: 10,
    title: "The Program",
    kicker: "room 10 / how it's built",
    lede:
      "The shape of the theory: four layers, one synthesis, one publication discipline. The room where the framework explains its own architecture and what it is allowed to claim.",
    kind: "program",
    epigram:
      "A general theory of relational trust infrastructure — and one paper that earns the right to say so.",
  },
];

/** Look up a room by URL slug. */
export function roomBySlug(slug: string): Room | undefined {
  return ROOMS.find((r) => r.slug === slug);
}
